import { useEffect, useRef, useState } from "react";
import { API_BASE } from "../config";
import { toast } from "../ui/Toast";
import { Close, Plus } from "../ui/Icons";
import { shrink, type Picked } from "../ui/shrink";
import type { Words } from "./copy";
import { siteUrl } from "./chrome";
import type { Language, PreviewDoc } from "../types";

type Check = NonNullable<PreviewDoc["photoCheck"]>;

/** The photos showed fewer people or animals than the form asked for, so the
 *  drawing is on hold and the customer is the one who can move it.
 *
 *  Three ways on, all of them theirs: add a photo that shows what is missing
 *  (the server reads the new set and either lets the drawing go or comes back
 *  here), carry on with what is already seen (Continue, whose label says what
 *  that will make), or start again from the form. The server decides which are
 *  open, through `canAdd` and `continueAs`, and this only draws them: a page
 *  that guessed would offer a button the endpoint then refuses.
 *
 *  Nothing here names what was seen or how it was read, only what is missing.
 *  The reading is a guess and is allowed to be wrong, so the card asks rather
 *  than tells. */

/** As the lead form shrinks them, so a photo added here weighs what it would
 *  have weighed there. */
const LONG_EDGE = 1600;
const QUALITY = 0.85;

/** The most photos one call takes, as the endpoint does. */
const PER_CALL = 3;

/** The Builder's slug in each language, the folder the site keeps it in
 *  (MiniMeWonder data/pages.json "create"). */
const BUILDER: Record<Language, string> = {
  en: "create/",
  pt: "criar/",
  nl: "maken/",
  de: "gestalten/",
  fr: "creer/",
  it: "crea/",
  es: "crear/",
  zh: "create/",
  "zh-hans": "create/",
};

/** The Builder opened on what they asked for, with the same query it reads
 *  (MiniMeWonder/src/lib/builder.ts): kind, and the people and pets of a
 *  statue. A keychain is one subject, so a person is one person and no pets and
 *  a pet the other way round. Whatever they never chose is left off, so the
 *  Builder does not start on a number they did not pick. */
export function newRequestUrl(language: Language, check: Check): string {
  const asked = check.requested;
  const params: Record<string, string> = { kind: check.kind };
  if (check.kind === "keychain") {
    if (asked.subject) {
      params.people = asked.subject === "person" ? "1" : "0";
      params.pets = asked.subject === "pet" ? "1" : "0";
    }
  } else {
    if (asked.people !== undefined) params.people = String(asked.people);
    if (asked.pets !== undefined) params.pets = String(asked.pets);
    // A statue of the animals alone reaches us as pets with no people at all,
    // and the Builder opens on one person unless it is told otherwise.
    if (asked.people === undefined && asked.pets !== undefined) params.people = "0";
  }
  return siteUrl(language, BUILDER[language], {
    ...params,
    utm_source: "preview",
    utm_medium: "photocheck",
  });
}

/** The one sentence on what is missing, as a copy key. A second person or pet
 *  is told apart from none at all, since "I can't see a pet" would be wrong
 *  to someone whose photo has one. */
function sentenceKey(check: Check): string {
  const { missing, requested, kind } = check;
  if (kind === "keychain") {
    if (requested.subject === "person" && missing.people > 0) return "pcKeyPerson";
    if (requested.subject === "pet" && missing.pets > 0) return "pcKeyPet";
    return "pcGeneric";
  }
  // Both missing is a person and a pet that were each asked for; the person's
  // line leads, the pet's follows (see the caller).
  if (missing.people > 0) {
    if (missing.people >= 2) return "pcPeopleNone";
    return requested.people === 2 ? "pcPersonSecond" : "pcPersonNone";
  }
  if (missing.pets > 0) return petKey(check);
  return "pcGeneric";
}

/** The pets' sentence. Up to two asked, "the second" or "none" says it; a
 *  statue of three or four animals alone with some of them seen says how
 *  many of how many (pcPetsSome, with {seen} and {n}). */
function petKey(check: Check): string {
  const asked = check.requested.pets ?? 0;
  const seen = asked - check.missing.pets;
  if (asked > 2 && seen > 0) return "pcPetsSome";
  if (check.missing.pets >= 2) return "pcPetsNone";
  return asked === 2 ? "pcPetSecond" : "pcPetNone";
}

function sentences(check: Check): { key: string; vars?: Record<string, string> }[] {
  const asked = check.requested.pets ?? 0;
  const said = (key: string) =>
    key === "pcPetsSome"
      ? { key, vars: { seen: String(asked - check.missing.pets), n: String(asked) } }
      : { key };
  const first = sentenceKey(check);
  if (check.kind === "statue" && check.missing.people > 0 && check.missing.pets > 0) {
    return [said(first), said(petKey(check))];
  }
  return [said(first)];
}

/** What Continue will make, in words. */
function continueKey(check: Check): string | null {
  switch (check.continueAs) {
    case "pet_figure":
      return check.continuePets >= 2 ? "pcContinuePets" : "pcContinuePet";
    case "minime":
      return "pcContinueMinime";
    case "duo":
      return "pcContinueDuo";
    case "pet_keychain":
      return "pcContinuePetKey";
    case "head_keychain":
      return "pcContinueHeadKey";
    default:
      return null;
  }
}

/** What an answer from /api/photo-check says to the customer. */
function refusalKey(status: number, error: string | undefined): string {
  if (status === 409) return "pcErrState";
  if (status === 429 || error === "rate") return "pcErrRate";
  if (status === 413 || error === "too-big") return "pcErrTooBig";
  if (error === "no-photos") return "pcErrNoPhotos";
  if (error === "too-many") return "pcErrTooMany";
  if (error === "unusable") return "pcErrUnusable";
  return "sendFailed";
}

export default function PhotoCheck({
  check,
  token,
  language,
  words: t,
  onChanged,
}: {
  check: Check;
  token: string;
  language: Language;
  words: Words;
  /** Read the page again: after anything the server may have moved. */
  onChanged: () => void;
}) {
  const [picked, setPicked] = useState<Picked[]>([]);
  const [preparing, setPreparing] = useState(false);
  const [busy, setBusy] = useState<"add" | "accept" | null>(null);
  /** The new photos are in and the page has not caught up with it yet, so the
   *  checking state shows at once rather than the old buttons for a moment. It
   *  is let go when the page says "rechecking" itself, or after a while. */
  const [justSent, setJustSent] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  // The thumbnails' URLs go when the photos do.
  const urls = useRef<string[]>([]);
  urls.current = picked.flatMap((p) => (p.url ? [p.url] : []));
  useEffect(() => () => urls.current.forEach((url) => URL.revokeObjectURL(url)), []);

  useEffect(() => {
    if (!justSent) return;
    const timer = window.setTimeout(() => setJustSent(false), 12000);
    return () => window.clearTimeout(timer);
  }, [justSent]);
  useEffect(() => {
    if (check.state === "rechecking") setJustSent(false);
  }, [check.state]);

  const room = Math.max(0, Math.min(PER_CALL, check.roomLeft) - picked.length);
  const checking = check.state === "rechecking" || justSent;
  const continuing = continueKey(check);

  async function add(files: FileList | null) {
    const all = Array.from(files ?? []);
    const list = all.slice(0, room);
    if (all.length > list.length) {
      toast(t.t("pcSomeLeft", { n: String(room) }), "plain", 5000);
    }
    if (!list.length) return;
    setPreparing(true);
    try {
      // One at a time: each is a full-size phone photo opened in memory.
      for (const file of list) {
        const prepared = await shrink(file, LONG_EDGE, QUALITY);
        setPicked((current) => [...current, prepared]);
      }
    } finally {
      setPreparing(false);
    }
  }

  function remove(id: string) {
    setPicked((current) => {
      const gone = current.find((p) => p.id === id);
      if (gone?.url) URL.revokeObjectURL(gone.url);
      return current.filter((p) => p.id !== id);
    });
  }

  /** One call to the endpoint. True when it went through; otherwise the
   *  customer has been told why, and a 409 has the page read again, since it
   *  means the request is no longer where this card thought it was. */
  async function call(action: "add" | "accept"): Promise<boolean> {
    setBusy(action);
    try {
      const body = new FormData();
      body.set("token", token);
      body.set("action", action);
      if (action === "add") picked.forEach((p) => body.append("photos", p.blob, p.name));
      const response = await fetch(`${API_BASE}/api/photo-check`, { method: "POST", body });
      if (response.ok) return true;
      const answer = (await response.json().catch(() => ({}))) as { error?: string };
      toast(t.t(refusalKey(response.status, answer.error)), "bad", 6000);
      if (response.status === 409) onChanged();
      return false;
    } catch {
      toast(t.t("sendFailed"), "bad");
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function send() {
    if (!picked.length) {
      toast(t.t("pcErrNoPhotos"), "plain");
      return;
    }
    if (!(await call("add"))) return;
    picked.forEach((p) => p.url && URL.revokeObjectURL(p.url));
    setPicked([]);
    setJustSent(true);
    onChanged();
  }

  async function carryOn() {
    if (!(await call("accept"))) return;
    toast(t.t("pcStarted"), "good");
    onChanged();
  }

  if (checking) {
    return (
      <div className="card stack">
        <h1>{t.t("pcChecking")}</h1>
        <div role="status" aria-live="polite" style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span className="spin" aria-hidden="true" />
          <p className="muted" style={{ margin: 0 }}>{t.t("pcCheckingWhat")}</p>
        </div>
      </div>
    );
  }

  const busyAny = busy !== null || preparing;
  return (
    <div className="card stack">
      <h1>{t.t("pcTitle")}</h1>
      <div role="status" aria-live="polite" className="stack" style={{ gap: 6 }}>
        {sentences(check).map(({ key, vars }) => (
          <p key={key} style={{ margin: 0 }}>{t.t(key, vars)}</p>
        ))}
      </div>

      {check.canAdd ? (
        <>
          <p className="muted" style={{ margin: 0 }}>
            {t.t(continuing ? "pcAskOrContinue" : "pcAsk")}
          </p>
          {picked.length > 0 && (
            <div className="thumbs">
              {picked.map((p) => (
                <div key={p.id} style={{ position: "relative" }}>
                  {p.url ? (
                    <img className="thumb" src={p.url} alt="" />
                  ) : (
                    <div className="thumb thumb-empty tiny muted">{p.name.slice(-12)}</div>
                  )}
                  <button
                    type="button"
                    className="thumb-remove"
                    aria-label={t.t("pcRemove")}
                    onClick={() => remove(p.id)}
                  >
                    <Close size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            className="btn btn-soft btn-block"
            disabled={room === 0 || busyAny}
            onClick={() => picker.current?.click()}
          >
            {preparing ? <span className="spin" /> : <Plus size={17} />}
            {preparing ? t.t("pcPreparing") : t.t("pcAdd")}
          </button>
          <input
            ref={picker}
            type="file"
            hidden
            multiple
            accept="image/*"
            aria-label={t.t("pcPhotosLabel")}
            onChange={(e) => {
              void add(e.target.files);
              e.target.value = "";
            }}
          />
          <p className="tiny muted" style={{ margin: 0 }}>
            {t.t("pcAddHint", { n: String(Math.min(PER_CALL, check.roomLeft)) })}
          </p>
          {picked.length > 0 && (
            <button type="button" className="btn btn-primary btn-block" disabled={busyAny} onClick={() => void send()}>
              {busy === "add" ? <span className="spin spin-on-red" /> : null}
              {t.t("pcSend")}
            </button>
          )}
        </>
      ) : (
        <p className="muted" style={{ margin: 0 }}>
          {t.t(continuing ? "pcNoMore" : "pcNoMoreNone")}
        </p>
      )}

      {continuing && (
        <>
          <button
            type="button"
            className={`btn btn-block ${picked.length ? "btn-soft" : "btn-primary"}`}
            disabled={busyAny}
            onClick={() => void carryOn()}
          >
            {busy === "accept" ? <span className="spin" /> : null}
            {t.t(continuing)}
          </button>
          <p className="tiny muted" style={{ margin: 0 }}>{t.t("pcContinueNote")}</p>
        </>
      )}

      <a className="btn btn-ghost btn-block" href={newRequestUrl(language, check)}>
        {t.t("pcNew")}
      </a>
    </div>
  );
}
